/**
 * Initialise GlitchTip.
 */

import type { Transport, TransportMakeRequestResponse, TransportRequest } from "@sentry/core";

import { pipe } from "@mobily/ts-belt";
import {
  breadcrumbsIntegration,
  BrowserClient,
  captureConsoleIntegration,
  createTransport,
  dedupeIntegration,
  defaultStackParser,
  functionToStringIntegration,
  getCurrentScope,
  globalHandlersIntegration,
  httpClientIntegration,
  httpContextIntegration,
  linkedErrorsIntegration,
} from "@sentry/browser";

import type { BrowserTransportOptions } from "./lib/types/sentry";

import { ENTETE_GLITCHTIP_RATE_LIMITS, ENTETE_GLITCHTIP_RETRY_AFTER } from "./constantes/api";

/* Créé la fonction génératrice de la requête auprès de GlitchTip */
const creeTransportFetch = (options: BrowserTransportOptions): Transport => {
  const creeRequete = async (requete: TransportRequest): Promise<TransportMakeRequestResponse> => {
    const optionsRequete: RequestInit = {
      body: requete.body,
      headers: options.headers ?? {},
      method: "POST",
      mode: "no-cors",
      referrerPolicy: "no-referrer",
      ...options.fetchOptions,
    };

    return pipe(
      await fetch(options.url, optionsRequete),
      (reponse: Response) => ({
        headers: {
          [ENTETE_GLITCHTIP_RATE_LIMITS]: reponse.headers.get(ENTETE_GLITCHTIP_RATE_LIMITS),
          [ENTETE_GLITCHTIP_RETRY_AFTER]: reponse.headers.get(ENTETE_GLITCHTIP_RETRY_AFTER),
        },
        statusCode: reponse.status,
      }),
    );
  };

  return createTransport(options, creeRequete);
};

/* Créé la configuration Sentry */
const client = new BrowserClient({
  dsn: import.meta.env.VITE_GLITCHTIP_NSD,
  environment: import.meta.env.VITE_MODE,
  integrations: [
    breadcrumbsIntegration(),
    captureConsoleIntegration({ levels: ["warn", "error"] }),
    dedupeIntegration(),
    functionToStringIntegration(),
    globalHandlersIntegration(),
    httpClientIntegration(),
    httpContextIntegration(),
    linkedErrorsIntegration(),
  ],
  sendDefaultPii: true,
  stackParser: defaultStackParser,
  tracesSampleRate: 1,
  transport: creeTransportFetch,
});

/* Initialise la configuration */
getCurrentScope().setClient(client);
client.init();
